import { toast } from 'react-toastify';

import axios from 'axios';
import * as Rx from 'rxjs';
import { debounceTime, map, switchMap, take, tap } from 'rxjs/operators';
import { URL_ORBITA } from 'settings';
import type { IInvoice, IInvoiceItem, IOrder, IOrderStatus } from 'v2/components/Pages/Orders/interfaces';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';

import initialValues, { getInitialValues } from '../../components/Pages/Orders/Filter/initialValues';
import type { ApiService } from '../api';
import apiService from '../api';
import type IFilter from './../../components/Pages/Orders/Filter/interfaces';
import { ECustomerSearchTypes } from './constants';

const AWAITING_SHIPMENT_STATUS = 'Aguardando Envio';

class OrdersService {
  private orders$ = new Rx.BehaviorSubject<IOrder[]>([]);
  private loading$ = new Rx.BehaviorSubject<boolean>(false);
  private filter$ = new Rx.BehaviorSubject<IFilter>(initialValues);
  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>({ current_page: 1, per_page: 50 });
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);
  private orderStatus$ = new Rx.BehaviorSubject<IOrderStatus[]>([]);
  private totalItems$ = new Rx.BehaviorSubject<number>(0);
  //quickview
  private quickview$ = new Rx.BehaviorSubject<boolean>(false);
  private loadingQuickview$ = new Rx.BehaviorSubject<boolean>(false);
  private order$ = new Rx.BehaviorSubject<IOrder>(null);
  private invoice$ = new Rx.BehaviorSubject<IInvoice>(null);
  private loadingInvoiceItems$ = new Rx.BehaviorSubject<boolean>(false);
  private invoiceItems$ = new Rx.BehaviorSubject<IInvoiceItem[]>([]);
  //modal
  private modalRefund$ = new Rx.BehaviorSubject<boolean>(false);
  private modalTrackingCodes$ = new Rx.BehaviorSubject<boolean>(false);

  constructor(private apiService: ApiService) {}

  public getOrders = (): Rx.Observable<IOrder[]> => this.orders$;

  public getFilter = (): Rx.Observable<IFilter> => this.filter$;

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public getOrderStatus = (): Rx.Observable<IOrderStatus[]> => this.orderStatus$;

  public getTotalItems = (): Rx.Observable<number> => this.totalItems$;

  public getOrder = (): Rx.Observable<IOrder> => this.order$;

  public getInvoice = (): Rx.Observable<IInvoice> => this.invoice$;

  public getQuickview = (): Rx.Observable<boolean> => this.quickview$;

  public getInvoiceItems = (): Rx.Observable<IInvoiceItem[]> => this.invoiceItems$;

  public getModalRefund = (): Rx.Observable<boolean> => this.modalRefund$;

  public getModalTrackingCodes = (): Rx.Observable<boolean> => this.modalTrackingCodes$;

  public isLoading = () => this.loading$;

  public isLoadingQuickview = () => this.loadingQuickview$;

  public isLoadingInvoiceItems = () => this.loadingInvoiceItems$;

  public correctFilter = (filter: IFilter) => {
    const customer: { customer_email?: string; customer_name?: string; document_number?: string } = {};

    if (filter.customer) {
      switch (filter.customer_type) {
        case ECustomerSearchTypes.EMAIL:
          customer['customer_email'] = filter.customer;
          break;
        case ECustomerSearchTypes.NAME:
          customer['customer_name'] = filter.customer;
          break;
        case ECustomerSearchTypes.DOCUMENT:
          customer['document_number'] = filter.customer;
          break;
        default:
          break;
      }
    }

    return {
      created_at: filter.date[0],
      created_at_final: filter.date[1],
      ...filter,
      ...customer,
      ...{ date: null }
    };
  };

  public setFilter(filter: IFilter): void {
    this.filter$.pipe(take(1)).subscribe((currentFilter: IFilter) => {
      this.filter$.next({ ...currentFilter, ...filter });
    });
  }

  public resetPage() {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });

    return this.getPaginationParams();
  }

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public loadOrderStatus() {
    this.apiService
      .get('/orders/filters/status')
      .pipe(
        map(response =>
          response.data.map((a: { name: string | number; id: string | number }) => ({ label: a.name, value: a.id }))
        )
      )
      .subscribe((orderStatus: IOrderStatus[]) => this.orderStatus$.next(orderStatus));
  }

  public load() {
    return this.getFilter().pipe(
      map(f => this.correctFilter(f)),
      switchMap(filter => this.resetPage().pipe(map(params => ({ ...filter, ...params })))),
      tap(() => this.loading$.next(true)),
      debounceTime(10),
      switchMap(filter => this.apiService.get('orders', filter)),
      tap(orders => this.orders$.next(orders.data)),
      tap(orders => this.paginationResponse$.next(orders.pagination)),
      tap(orders => this.totalItems$.next(orders?.pagination?.total_items)),
      tap(() => this.loading$.next(false))
    );
  }

  public loadInvoice(order: IOrder) {
    return this.apiService
      .get(`/invoices/${order.invoice_id}`)
      .pipe(map(response => this.invoice$.next(response.data)));
  }

  public loadInvoiceItems() {
    this.loadingInvoiceItems$.next(true);
    return this.getInvoice().pipe(
      switchMap(invoice =>
        this.apiService.get(`/orders/${invoice.id}/items`).pipe(map(response => this.invoiceItems$.next(response.data)))
      ),
      tap(() => this.loadingInvoiceItems$.next(false))
    );
  }

  public openQuickview(order: IOrder) {
    this.quickview$.next(true);
    this.loadingQuickview$.next(true);
    this.order$.next(order);
    return this.loadInvoice(order).pipe(tap(() => this.loadingQuickview$.next(false)));
  }

  public closeQuickview() {
    this.order$.next(null);
    this.invoice$.next(null);
    this.quickview$.next(false);
  }

  public export() {
    return this.apiService.get('orders', { ...this.correctFilter(this.filter$.value), ...{ export: true } });
  }

  public openModalRefund() {
    this.modalRefund$.next(true);
  }

  public closeModalRefund() {
    this.modalRefund$.next(false);
  }

  public openModalTrackingCodes() {
    this.modalTrackingCodes$.next(true);
  }

  public closeModalTrackingCodes() {
    this.modalTrackingCodes$.next(false);
    return Rx.of(true);
  }

  public markAsSent() {
    return this.apiService.post(`/orders/${this.invoice$.value.id}/send`, {}).pipe(
      tap(() => {
        const order = this.order$.value;
        if (!order) return;

        const updatedOrder = { ...order, status: 'Enviado' };
        this.order$.next(updatedOrder);
        this.orders$.next(
          this.orders$.value.map(item => (item.invoice_id === updatedOrder.invoice_id ? updatedOrder : item))
        );
      }),
      tap(() => this.loadInvoice(this.order$.value))
    );
  }

  public checkOrder(invoiceId: number) {
    const orders = this.orders$.value;
    const newOrders = orders.map(o => {
      if (o.invoice_id === invoiceId) o.checked = !o.checked;
      return o;
    });
    this.orders$.next(newOrders);
  }

  public checkAllOrders() {
    const orders = this.orders$.value;
    const newOrders = orders.map(o => {
      o.checked = true;
      return o;
    });
    this.orders$.next(newOrders);
  }

  public unCheckAllOrders() {
    const orders = this.orders$.value;
    const newOrders = orders.map(o => {
      o.checked = false;
      return o;
    });
    this.orders$.next(newOrders);
  }

  public async generateLabel() {
    const orders = this.orders$.value;
    const invoices: string[] = orders.filter(o => o.checked).map(i => `invoice_id[]=${i.invoice_id}`);
    toast.success('Etiquetas geradas com sucesso');

    const response = await axios.get(`${URL_ORBITA}/orders/tags?return_url=1&${invoices.join('&')}`);
    window.open(response.data.data.download_url, '_blank');
  }

  public sendDeliveryCodeInvoiceItem(invoiceItem: IInvoiceItem) {
    return this.apiService.put(`/orders/${this.invoice$.value.id}/items/${invoiceItem.id}`, {
      traking_code: invoiceItem.order.traking_code
    });
  }

  public sendDeliveryCodeOrder(order: IOrder) {
    return this.apiService.put(`/orders/${order.invoice_id}`, { traking_code: order.traking_code }).pipe(
      tap(() => {
        this.orders$.next(
          this.orders$.value.map(item =>
            item.invoice_id === order.invoice_id
              ? { ...item, status: 'Enviado', traking_code: order.traking_code }
              : item
          )
        );
      })
    );
  }

  public getOrdersToSendDeliveryCode = (): Rx.Observable<IOrder[]> =>
    this.orders$.pipe(map(orders => (orders || []).filter(order => order.status === AWAITING_SHIPMENT_STATUS)));

  public cleanFilter() {
    this.filter$.next(getInitialValues());
  }
}

const ordersService = new OrdersService(apiService);
export default ordersService;
